import React, { useEffect, useState } from "react";
import api, { API, formatCurrency, COUNTRY_FLAGS } from "@/lib/api";
import { X, Loader2, Download, ExternalLink, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatPayoutDestination } from "@/lib/payoutFields";

/**
 * Admin-only modal showing the FULL transaction including unmasked card details
 * and downloadable card images.
 */
export default function TxDetailModal({ txId, onClose, onUpdated }) {
  const [tx, setTx] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!txId) return;
    setTx(null);
    api.get(`/admin/transactions/${txId}/detail`)
      .then((r) => {
        setTx(r.data);
        setNote(r.data.admin_note || "");
      })
      .catch(() => toast.error("Failed to load transaction"));
  }, [txId]);

  if (!txId) return null;

  const update = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/transactions/${txId}`, { status, admin_note: note || null });
      toast.success(`Status set to ${status}`);
      onUpdated && onUpdated();
      onClose();
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const card = tx?.card_details || {};

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="admin-tx-modal">
      <div className="bg-white rounded-3xl max-w-3xl w-full shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Transaction details</p>
            <p className="font-display text-xl font-bold text-[#0B3C5D]" data-testid="admin-modal-tx-id">{txId}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="admin-modal-close" aria-label="Close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {!tx ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-[#0B3C5D] animate-spin" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {tx.risk_flags && tx.risk_flags.length > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-sm">
                <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />
                <div>
                  <p className="font-semibold text-amber-800">Risk flags</p>
                  <p className="text-amber-700 text-xs">{tx.risk_flags.join(", ")}</p>
                </div>
              </div>
            )}

            <Section title="Submission">
              <Row label="User" value={tx.user_email} />
              <Row label="Brand" value={tx.brand_name} />
              <Row label="Country" value={`${COUNTRY_FLAGS[tx.country] || ""} ${tx.country}`} />
              <Row label="Card value" value={formatCurrency(tx.amount, tx.currency)} />
              <Row label="Rate" value={`${(tx.rate_pct * 100).toFixed(1)}%`} />
              <Row label="Gross payout" value={formatCurrency(tx.gross_payout, tx.currency)} />
              <Row label="Processing fee" value={`- ${formatCurrency(tx.processing_fee, tx.currency)}`} />
              <Row label="Final payout" value={formatCurrency(tx.final_payout, tx.currency)} accent />
              <Row label="Payout method" value={tx.payout_method_name} />
              <Row label="Payout destination" value={formatPayoutDestination(tx.payout_method, tx.payout_destination)} mono highlight />
              <Row label="Status" value={tx.status} />
              <Row label="Submitted" value={new Date(tx.created_at).toLocaleString()} />
            </Section>

            <Section title="Card details (admin view)">
              <Row label="Condition" value={card.condition} />
              <Row label="Format" value={card.format} />
              <Row label="Card number" value={card.card_number || "—"} mono highlight />
              <Row label="PIN / CVV" value={card.pin || "—"} mono highlight />
              <Row label="Expiration" value={card.expiration || "—"} />
              <Row label="Phone" value={card.phone || "—"} />
              <Row label="Remaining balance" value={card.remaining_balance != null ? formatCurrency(card.remaining_balance, tx.currency) : "—"} />
              <Row label="Receipt available" value={card.receipt_available == null ? "—" : card.receipt_available ? "Yes" : "No"} />
              <Row label="Purchase method" value={card.purchase_method || "—"} />
              <Row label="Activation status" value={card.activation_status || "—"} />
              <Row label="Region restriction" value={card.region_restriction || "—"} />
              <Row label="Delivery method" value={card.delivery_method || "—"} />
              <Row label="Date purchased" value={card.date_purchased || "—"} />
              <Row label="Store of purchase" value={card.store_of_purchase || "—"} />
            </Section>

            <Section title={`Uploaded images (${tx.uploads?.length || 0})`}>
              {!tx.uploads || tx.uploads.length === 0 ? (
                <p className="text-sm text-slate-500">No images uploaded with this submission.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {tx.uploads.map((u) => (
                    <ImageTile key={u.file_id} upload={u} />
                  ))}
                </div>
              )}
            </Section>

            <Section title="Admin note">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add an internal note for this transaction (visible to the customer in their tracker)…"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                rows={3}
                data-testid="admin-modal-note"
              />
            </Section>

            <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
              <button disabled={busy} onClick={() => update("verifying")} className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 disabled:opacity-50" data-testid="admin-modal-verify">Verifying</button>
              <button disabled={busy} onClick={() => update("approved")} className="text-xs px-3 py-1.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 disabled:opacity-50" data-testid="admin-modal-approve">Approve</button>
              <button disabled={busy} onClick={() => update("paid")} className="text-xs px-3 py-1.5 rounded-full bg-[#00C853]/10 text-[#00a543] border border-[#00C853]/30 disabled:opacity-50 flex items-center gap-1" data-testid="admin-modal-paid"><CheckCircle2 className="w-3 h-3" /> Mark Paid</button>
              <button disabled={busy} onClick={() => update("rejected")} className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 disabled:opacity-50" data-testid="admin-modal-reject">Reject</button>
              {busy && <Loader2 className="w-4 h-4 text-slate-500 animate-spin" />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ImageTile({ upload }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const resp = await api.get(`/uploads/${upload.file_id}`, { responseType: "blob" });
        if (cancelled) return;
        createdUrl = URL.createObjectURL(resp.data);
        setBlobUrl(createdUrl);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upload.file_id]);

  const download = async () => {
    try {
      const resp = await api.get(`/uploads/${upload.file_id}?download=1`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = upload.filename || `${upload.file_id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50" data-testid={`admin-img-tile-${upload.file_id}`}>
      <div className="aspect-[1.5/1] bg-slate-100 flex items-center justify-center">
        {loading ? (
          <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
        ) : blobUrl ? (
          <img src={blobUrl} alt="Submitted card" className="w-full h-full object-cover" />
        ) : (
          <p className="text-xs text-slate-400">Unavailable</p>
        )}
      </div>
      <div className="p-2 flex items-center justify-between text-xs">
        <span className="truncate text-slate-600 mr-2" title={upload.filename}>{upload.filename || upload.file_id.slice(0, 8)}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {blobUrl && (
            <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-slate-200" aria-label="Open" data-testid={`admin-img-open-${upload.file_id}`}>
              <ExternalLink className="w-3.5 h-3.5 text-slate-600" />
            </a>
          )}
          <button onClick={download} className="p-1.5 rounded-lg hover:bg-slate-200" aria-label="Download" data-testid={`admin-img-download-${upload.file_id}`}>
            <Download className="w-3.5 h-3.5 text-[#0B3C5D]" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, accent, mono, highlight }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm py-1">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right break-all ${mono ? "font-mono" : ""} ${accent ? "font-display font-bold text-[#0B3C5D]" : highlight ? "text-[#0B3C5D] font-semibold bg-amber-50 px-2 py-0.5 rounded" : "text-slate-900"}`}>
        {value}
      </span>
    </div>
  );
}
